import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type Guid from "_shared/guids/guid";
import type { GrantRowModel } from "permissions/_views/grant-row/grant-row-model";
import GrantHistory from "permissions/_views/people/grant-history";
import { BY_THE_SYSTEM, FROM, kindAllows, kindLabel, recordDetailTerms } from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import type { ReactNode } from "react";

/**
 * The details pane at xl: the width a capped page leaves over, spent on the row the reader chose with
 * "Show details". It carries what a row has no room for: the whole source chain, the note, the
 * grant's id, why it is locked, and the grants it replaced. A per-record row also names its record and
 * the record's id.
 */

/** The row the pane describes. `target` is set for a per-record grant: the record's name and id. */
export type GrantDetailsSelection = Readonly<{
  row: GrantRowModel;
  permission?: Permission;
  target?: Readonly<{ label?: string; id?: string; noun: string }>;
}>;

function Fact(props: Readonly<{ term: string; children: ReactNode }>) {
  return (
    <>
      <Typography component="dt" variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        {props.term}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: "anywhere" }}>
        {props.children}
      </Typography>
    </>
  );
}

function sourceText(row: GrantRowModel): string | undefined {
  const source = row.source;
  if (!source) return undefined;
  if (source.type === "parents") return `${FROM} ${source.parents.map((parent) => parent.label).join(" + ")}`;
  if (source.type === "unnamed-grantor") return source.stateText ? `${source.label} · ${source.stateText}` : source.label;
  return source.label;
}

/**
 * What the pane shows of a selection, as one string, so a list can tell whether its fresher copy of
 * the selected grant would change the pane before it replaces the selection.
 */
export function detailsSignature(selection: GrantDetailsSelection | undefined): string {
  if (!selection) return "";
  const { row, permission, target } = selection;
  return [
    permission?.id?.value,
    row.state,
    row.levelText,
    row.removedText,
    sourceText(row),
    row.lock?.explanation,
    permission?.note,
    target?.label,
    target?.id,
  ].join("|");
}

/**
 * `viewerUserId` turns on the grant's history (#6825), which is read from the server; a pane drawn
 * without it shows only what the row already holds.
 */
export default function PersonGrantDetails(props: Readonly<{ selection?: GrantDetailsSelection; viewerUserId?: Guid }>) {
  const { row, permission, target } = props.selection ?? {};

  return (
    <Box
      component="aside"
      aria-label="Grant details"
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, backgroundColor: "background.paper" }}
    >
      <Typography variant="overline" color="text.secondary" component="h2">
        Details
      </Typography>
      {row ? (
        <Box component="dl" sx={{ m: 0 }}>
          <Typography variant="subtitle1" component="p" sx={{ fontWeight: 500 }}>
            {kindLabel(row.kind)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {kindAllows(row.kind)}
          </Typography>
          {target?.label && <Fact term={recordDetailTerms.name(target.noun)}>{target.label}</Fact>}
          {target?.id && <Fact term={recordDetailTerms.id(target.noun)}>{target.id}</Fact>}
          <Fact term="Level">{row.levelText}</Fact>
          {sourceText(row) && <Fact term="Source">{sourceText(row)}</Fact>}
          {row.removedText && <Fact term="State">{row.removedText}</Fact>}
          {row.lock && <Fact term={row.lock.word}>{row.lock.explanation}</Fact>}
          {permission?.note && row.source?.type !== "system" && <Fact term="Note">{permission.note}</Fact>}
          {row.source?.type === "system" && row.source.note && <Fact term={BY_THE_SYSTEM}>{row.source.note}</Fact>}
          {permission?.id && <Fact term="Grant id">{permission.id.value}</Fact>}
          {permission?.id && props.viewerUserId && <GrantHistory permissionId={permission.id} viewerUserId={props.viewerUserId} />}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {`Choose "Show details" in a row's menu to see where its grant came from.`}
        </Typography>
      )}
    </Box>
  );
}
